import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Download, Shield, HelpCircle } from 'lucide-react';
import { NotificationSettings } from '../components/PushNotifications';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { toast } from 'sonner';

export default function Settings() {
  const navigate = useNavigate();

  const handleExportData = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/scan/history/export?format=${format}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const ext = format === 'pdf' ? 'pdf' : 'csv';
      const mimeType = format === 'pdf' ? 'application/pdf' : 'text/csv';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `greenlight_history_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'No scan history to export' : 'Export failed');
    }
  };

  return (
    <div 
      className="min-h-screen bg-qc-bg text-qc-text"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
    >
      {/* Header */}
      <div className="px-5 py-3 flex items-center max-w-lg mx-auto">
        <button
          onClick={() => navigate('/home')}
          data-testid="settings-back-button"
          className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-black text-qc-text ml-3 tracking-tight">Settings</h1>
      </div>

      <div className="max-w-lg mx-auto px-5 py-4 space-y-6" data-testid="settings-page">
        {/* Notifications Section */}
        <section>
          <h2 className="text-sm font-bold text-qc-text-dim uppercase tracking-wider mb-3 flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notifications
          </h2>
          <NotificationSettings />
        </section>

        {/* Export Section */}
        <section>
          <h2 className="text-sm font-bold text-qc-text-dim uppercase tracking-wider mb-3 flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Your Data
          </h2>
          <div className="bg-qc-surface rounded-xl border border-qc-border p-4">
            <p className="text-sm text-qc-text-dim mb-4">
              Download your complete scan history as a spreadsheet or printable report.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => handleExportData('csv')}
                variant="outline"
                size="sm"
                data-testid="settings-export-csv"
                className="flex-1 h-10 border-qc-border text-qc-text"
              >
                <span className="text-qc-green font-bold mr-1">CSV</span> Spreadsheet
              </Button>
              <Button
                onClick={() => handleExportData('pdf')}
                variant="outline"
                size="sm"
                data-testid="settings-export-pdf"
                className="flex-1 h-10 border-qc-border text-qc-text"
              >
                <span className="text-qc-red font-bold mr-1">PDF</span> Report
              </Button>
            </div>
          </div>
        </section>

        {/* Privacy Section */}
        <section>
          <h2 className="text-sm font-bold text-qc-text-dim uppercase tracking-wider mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Privacy & Legal
          </h2>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/privacy')}
              className="w-full flex items-center justify-between p-4 bg-qc-surface rounded-xl border border-qc-border text-left"
            >
              <span className="text-sm font-medium text-qc-text">Privacy Policy</span>
              <ArrowLeft className="w-4 h-4 text-qc-text-faint rotate-180" />
            </button>
            <button
              onClick={() => navigate('/terms')}
              className="w-full flex items-center justify-between p-4 bg-qc-surface rounded-xl border border-qc-border text-left"
            >
              <span className="text-sm font-medium text-qc-text">Terms of Service</span>
              <ArrowLeft className="w-4 h-4 text-qc-text-faint rotate-180" />
            </button>
          </div>
        </section>

        {/* Help Section */}
        <section>
          <h2 className="text-sm font-bold text-qc-text-dim uppercase tracking-wider mb-3 flex items-center gap-2">
            <HelpCircle className="w-4 h-4" />
            Help & Support
          </h2>
          <button
            onClick={() => navigate('/contact')}
            className="w-full flex items-center justify-between p-4 bg-qc-surface rounded-xl border border-qc-border text-left"
          >
            <span className="text-sm font-medium text-qc-text">Contact Us</span>
            <ArrowLeft className="w-4 h-4 text-qc-text-faint rotate-180" />
          </button>
        </section>

        {/* App Info */}
        <div className="text-center pt-4 pb-8">
          <p className="text-xs text-qc-text-faint">
            Get Green Light v1.0
          </p>
        </div>
      </div>
    </div>
  );
}
